import { useEffect, useRef } from 'react'

import { useSafeSetParams } from './useSafeSetParams'

export type TUseSearchParamsActionProps<ParamNames extends string> = {
  searchParams: Record<string, string | string[] | undefined>
  action: (matchedSearchParams: Record<ParamNames, string | string[] | undefined>) => void
  /** Set to `null` to disable */
  requestedQuery: { [K in ParamNames]: string | ((value: string | string[] | undefined) => boolean) } | null
  keepQueryParams?: boolean
}

/**
 * Check if a key or a query object is included in the url query.
 * If it does, call run the action, then remove all the matches from the url.
 */
export const useSearchParamsAction = <ParamNames extends string>({
  searchParams,
  action,
  requestedQuery,
  keepQueryParams,
}: TUseSearchParamsActionProps<ParamNames>) => {
  const hasMatched = useRef(false)
  const { safeSetParams } = useSafeSetParams()

  // action must be run in an effect so that it can update state from other components
  useEffect(() => {
    if (
      !hasMatched.current &&
      requestedQuery &&
      searchParams &&
      Object.entries(requestedQuery).every(([key, value]) =>
        typeof value === 'string'
          ? key in searchParams && searchParams[key] === (value || '')
          : value(searchParams[key]),
      )
    ) {
      hasMatched.current = true

      const matchedSearchParams = Object.keys(requestedQuery).reduce<Record<ParamNames, string | string[] | undefined>>(
        (acc, key) => Object.assign(acc, { [key]: searchParams[key] }),
        {},
      )

      const undefinedRequestedQueryParams = Object.keys(requestedQuery).reduce<Record<string, string | undefined>>(
        (acc, key) => Object.assign(acc, { [key]: undefined }),
        {},
      )

      action(matchedSearchParams)
      if (!keepQueryParams) {
        safeSetParams(undefinedRequestedQueryParams)
      }
    }
  }, [action, keepQueryParams, requestedQuery, safeSetParams, searchParams])

  return null
}
